import {
  DISPLAY_AEROPAY,
  DISPLAY_APPLEPAY,
  DISPLAY_APPROVED,
  DISPLAY_BANK,
  DISPLAY_CARD,
  DISPLAY_COMPLETED,
  DISPLAY_DECLINED,
  DISPLAY_FAILED,
  DISPLAY_INITIATED,
  DISPLAY_PAYPAL,
  DISPLAY_PENDING_APPROVAL,
  DISPLAY_PROCESSING,
  DISPLAY_REFUNDED,
  DISPLAY_REJECTED,
  DISPLAY_VENMO,
  DISPLAY_VOIDED,
  DISPLAY_WITHDRAWN,
  METHOD_AEROPAY,
  METHOD_APPLEPAY,
  METHOD_BANK,
  METHOD_CARD,
  METHOD_PAYPAL,
  METHOD_VENMO,
  STATUS_APPROVED,
  STATUS_COMPLETED,
  STATUS_DECLINED,
  STATUS_FAILED,
  STATUS_INITIATED,
  STATUS_PENDING_APPROVAL,
  STATUS_PROCESSING,
  STATUS_REFUNDED,
  STATUS_REJECTED,
  STATUS_VOIDED,
  STATUS_WITHDRAWN
} from '@library/constants/payments';
import type {
  DepositMethod,
  DepositMethodDisplay,
  DepositStatus,
  DepositStatusDisplay,
  PaymentMethod,
  PaymentMethodDisplay,
  WithdrawalStatus,
  WithdrawalStatusDisplay
} from '@library/types/payments';
import type { SelectOptions } from '@library/types/utils';
import { toTitleCase } from '@bo-utils/common';

export const depositStatusOptions: SelectOptions = [
  {
    key: STATUS_COMPLETED,
    name: DISPLAY_COMPLETED,
    value: STATUS_COMPLETED
  },
  {
    key: STATUS_DECLINED,
    name: DISPLAY_DECLINED,
    value: STATUS_DECLINED
  },
  {
    key: STATUS_FAILED,
    name: DISPLAY_FAILED,
    value: STATUS_FAILED
  },
  {
    key: STATUS_INITIATED,
    name: DISPLAY_INITIATED,
    value: STATUS_INITIATED
  },
  {
    key: STATUS_PROCESSING,
    name: DISPLAY_PROCESSING,
    value: STATUS_PROCESSING
  },
  {
    key: STATUS_REFUNDED,
    name: DISPLAY_REFUNDED,
    value: STATUS_REFUNDED
  }
];

export const depositMethodTypeOptions: SelectOptions = [
  {
    key: METHOD_APPLEPAY,
    name: DISPLAY_APPLEPAY,
    value: METHOD_APPLEPAY
  },
  {
    key: METHOD_AEROPAY,
    name: DISPLAY_BANK,
    value: METHOD_AEROPAY
  },
  {
    key: METHOD_CARD,
    name: DISPLAY_CARD,
    value: METHOD_CARD
  },
  {
    key: METHOD_PAYPAL,
    name: DISPLAY_PAYPAL,
    value: METHOD_PAYPAL
  },
  {
    key: METHOD_VENMO,
    name: DISPLAY_VENMO,
    value: METHOD_VENMO
  }
];

export const depositStatusDisplay = (
  status: DepositStatus
): DepositStatusDisplay => {
  switch (status) {
    case STATUS_PROCESSING:
      return DISPLAY_PROCESSING;
    case STATUS_FAILED:
      return DISPLAY_FAILED;
    case STATUS_DECLINED:
      return DISPLAY_DECLINED;
    case STATUS_REFUNDED:
      return DISPLAY_REFUNDED;
    case STATUS_COMPLETED:
      return DISPLAY_COMPLETED;
    case STATUS_INITIATED:
      return DISPLAY_INITIATED;
    default:
      return status;
  }
};

export const depositMethodDisplay = (
  depositMethod: DepositMethod
): DepositMethodDisplay => {
  switch (depositMethod) {
    case METHOD_CARD:
      return DISPLAY_CARD;
    case METHOD_AEROPAY:
      return DISPLAY_BANK;
    case METHOD_PAYPAL:
      return DISPLAY_PAYPAL;
    case METHOD_VENMO:
      return DISPLAY_VENMO;
    case METHOD_APPLEPAY:
      return DISPLAY_APPLEPAY;
    default:
      return depositMethod;
  }
};

interface Transformations {
  [name: string]: string;
}

const nameTransformations: Transformations = {
  VI: 'Visa',
  VD: 'Visa',
  DI: 'Discover',
  MC: 'Mastercard',
  CREDIT: 'Credit',
  DEBIT: 'Debit',
  PREPAID: 'Prepaid'
};

export const transformName = (name: string) => {
  const transformation = nameTransformations[name];
  if (transformation) {
    return transformation;
  }
  return toTitleCase(name);
};

export const withdrawalStatusOptions: SelectOptions = [
  {
    key: STATUS_APPROVED,
    name: DISPLAY_APPROVED,
    value: STATUS_APPROVED
  },
  {
    key: STATUS_DECLINED,
    name: DISPLAY_DECLINED,
    value: STATUS_DECLINED
  },
  {
    key: STATUS_FAILED,
    name: DISPLAY_FAILED,
    value: STATUS_FAILED
  },
  {
    key: STATUS_PENDING_APPROVAL,
    name: DISPLAY_PENDING_APPROVAL,
    value: STATUS_PENDING_APPROVAL
  },
  {
    key: STATUS_PROCESSING,
    name: DISPLAY_PROCESSING,
    value: STATUS_PROCESSING
  },
  {
    key: STATUS_REFUNDED,
    name: DISPLAY_REFUNDED,
    value: STATUS_REFUNDED
  },
  {
    key: STATUS_REJECTED,
    name: DISPLAY_REJECTED,
    value: STATUS_REJECTED
  },
  {
    key: STATUS_VOIDED,
    name: DISPLAY_VOIDED,
    value: STATUS_VOIDED
  },
  {
    key: STATUS_WITHDRAWN,
    name: DISPLAY_WITHDRAWN,
    value: STATUS_WITHDRAWN
  }
];

export const withdrawalMethodOptions: SelectOptions = [
  {
    key: METHOD_AEROPAY,
    name: DISPLAY_BANK,
    value: METHOD_AEROPAY
  },
  {
    key: METHOD_PAYPAL,
    name: DISPLAY_PAYPAL,
    value: METHOD_PAYPAL
  },
  {
    key: METHOD_VENMO,
    name: DISPLAY_VENMO,
    value: METHOD_VENMO
  }
];

export const withdrawalMethodDisplay = (
  method: PaymentMethod
): PaymentMethodDisplay => {
  switch (method) {
    case METHOD_PAYPAL:
      return DISPLAY_PAYPAL;
    case METHOD_VENMO:
      return DISPLAY_VENMO;
    case METHOD_APPLEPAY:
      return DISPLAY_APPLEPAY;
    case METHOD_BANK:
      return DISPLAY_BANK;
    case METHOD_AEROPAY:
      return DISPLAY_AEROPAY;
    default:
      return method;
  }
};

export const withdrawalStatusDisplay = (
  status: WithdrawalStatus
): WithdrawalStatusDisplay => {
  switch (status) {
    case STATUS_PENDING_APPROVAL:
      return DISPLAY_PENDING_APPROVAL;
    case STATUS_APPROVED:
      return DISPLAY_APPROVED;
    case STATUS_WITHDRAWN:
      return DISPLAY_WITHDRAWN;
    case STATUS_FAILED:
      return DISPLAY_FAILED;
    case STATUS_DECLINED:
      return DISPLAY_DECLINED;
    case STATUS_REFUNDED:
      return DISPLAY_REFUNDED;
    case STATUS_INITIATED:
      return DISPLAY_INITIATED;
    case STATUS_PROCESSING:
      return DISPLAY_PROCESSING;
    case STATUS_REJECTED:
      return DISPLAY_REJECTED;
    case STATUS_VOIDED:
      return DISPLAY_VOIDED;
    default:
      return status;
  }
};
